import React, { useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { DatePicker } from '@telus-uds/components-web';

/**
 * A thin wrapper around the UDS DatePicker that bridges the gap between
 * a parent that stores dates as strings (e.g. 'YYYY-MM-DD') and the
 * UDS DatePicker which expects a moment reference for its `date` prop.
 *
 * Strategy: Use a `key` derived from the value to force the UDS DatePicker
 * to **remount** whenever the value changes externally (filter reset, step
 * navigation, form load, etc.).  This completely sidesteps the UDS internal
 * sync-effect that relies on `date instanceof moment` and `isSame` checks
 * which can fail to update `inputText` in various edge cases.
 *
 * On each mount the UDS DatePicker initialises with the `date` prop, so
 * `inputText` is always correct.  When the user selects a day inside the
 * calendar, `onDateChange` fires but the key doesn't change
 * (the parent's value update round-trips through the wrapper and
 * produces the same key), so there's no extra remount.
 *
 * IMPORTANT: The webpack alias for 'moment' in webpack.common.babel.js must
 * point to the root node_modules/moment so that our moment and the UDS
 * DatePicker's moment share the same constructor (making instanceof work).
 *
 * Props:
 *   value         – date string (e.g. '2026-03-04') or falsy for "no date"
 *   onValueChange – callback receiving the formatted string (or '' on clear)
 *   parseFormat   – moment format used for the OUTPUT string (default 'YYYY-MM-DD')
 *   ...rest       – forwarded to <DatePicker> (id, label, dateFormat, etc.)
 */
const ManagedDatePicker = ({
  value,
  onValueChange,
  parseFormat = 'YYYY-MM-DD',
  id,
  ...rest
}) => {
  // The UDS DatePicker's calendar popup only repositions on scroll, it never
  // closes - so it ends up floating disconnected from its input as the page
  // scrolls. react-dates (the underlying lib) only closes the calendar on a
  // `focusout` whose `relatedTarget` (the element gaining focus) is outside
  // its own container - blurring to nothing/document.body doesn't qualify,
  // it has to be a genuine focus move to an unrelated element. So on a real
  // (user-triggered) scroll, move focus to a hidden sentinel button that
  // lives outside the calendar, forcing that outside-focus close path.
  // `isTrusted` skips synthetic scroll events some callers dispatch on focus
  // to force a reposition.
  const wrapperRef = useRef(null);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const handleScroll = (event) => {
      if (!event.isTrusted) return;
      const active = document.activeElement;
      if (!active) return;
      const insideThisPicker = wrapperRef.current && wrapperRef.current.contains(active);
      const insideOpenCalendar = !!active.closest('[aria-label="Calendar"]');
      if ((insideThisPicker || insideOpenCalendar) && sentinelRef.current) {
        sentinelRef.current.focus();
      }
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  // UDS's DatePicker always positions its calendar BELOW the input
  // (`top: inputTop + inputHeight`) with no viewport-edge detection, so a
  // date field near the bottom of a long page/form opens a calendar that
  // extends past the viewport and over the sticky footer. react-dates'
  // `.SingleDatePicker_picker` (the actual visible calendar box) carries its
  // own inline `top` relative to UDS's positioned portal ancestor, and its
  // height can still be 0/settling for the first frame or two after open, so
  // a single one-shot attempt can lock in a wrong result. On this input's
  // own focus: keep the calendar hidden (not merely mispositioned) until the
  // first frame where it has a real height, apply the above/below decision
  // then reveal - this avoids the "opens below, then visibly jumps above"
  // flash the naive always-visible version had. Re-check for a further ~1.5s
  // in case UDS re-asserts its own position later, always re-deriving "does
  // it fit below" from the input's own (stable) position rather than the
  // calendar's own possibly already-adjusted rect, so an earlier, premature
  // correction can never mask a later, more accurate one.
  useEffect(() => {
    const FOOTER_ALLOWANCE = 100;
    const GAP = 8;
    let revealed = false;

    const repositionIfNeeded = () => {
      const pickerEl = document.querySelector('.SingleDatePicker_picker');
      const inputEl = wrapperRef.current && wrapperRef.current.querySelector('input');
      if (!pickerEl || !inputEl) return;
      const rect = pickerEl.getBoundingClientRect();
      if (rect.height === 0) return;
      if (!revealed) pickerEl.style.visibility = 'hidden';

      const inputRect = inputEl.getBoundingClientRect();
      const maxBottom = window.innerHeight - FOOTER_ALLOWANCE;
      // react-dates' connector arrow is a sibling of the input (not a child
      // of the picker), so it doesn't move when we reposition the picker -
      // hide it whenever we flip above, since it would otherwise be left
      // pointing at the picker's old below-the-input spot (see screenshot).
      const fangEl = document.querySelector('.DateInput_fang');

      // UDS places the calendar directly below the input, so its natural
      // (unmodified) bottom edge would land here.
      const naturalBelowBottom = inputRect.bottom + rect.height;
      if (naturalBelowBottom <= maxBottom) {
        if (fangEl) fangEl.style.display = '';
      } else {
        const currentTop = parseFloat(pickerEl.style.top) || 0;
        const desiredViewportTop = inputRect.top - rect.height - GAP >= 0
          ? inputRect.top - rect.height - GAP
          : Math.max(0, maxBottom - rect.height); // doesn't fit above either - clamp within viewport
        const delta = desiredViewportTop - rect.top;
        if (Math.abs(delta) > 0.5) {
          pickerEl.style.top = `${currentTop + delta}px`;
        }
        if (fangEl) fangEl.style.display = 'none';
      }

      if (!revealed) {
        pickerEl.style.visibility = 'visible';
        revealed = true;
      }
    };

    let rafId = null;
    let stopTimeoutId = null;
    const loop = () => {
      repositionIfNeeded();
      rafId = requestAnimationFrame(loop);
    };

    const handleFocusIn = (event) => {
      if (!wrapperRef.current || !wrapperRef.current.contains(event.target)) return;
      revealed = false;
      if (rafId) cancelAnimationFrame(rafId);
      if (stopTimeoutId) clearTimeout(stopTimeoutId);
      rafId = requestAnimationFrame(loop);
      stopTimeoutId = setTimeout(() => {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = null;
      }, 1500);
    };

    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      if (rafId) cancelAnimationFrame(rafId);
      if (stopTimeoutId) clearTimeout(stopTimeoutId);
    };
  }, []);

  // Derive a moment synchronously from the value string.
  const dateMoment = value && moment(value).isValid() ? moment(value) : null;

  // Stable key: changes only when the *day* changes (or when clearing).
  // This forces the UDS DatePicker to remount and re-initialise its internal
  // inputText/inputDate from the date prop.
  const dateKey = dateMoment ? dateMoment.format('YYYY-MM-DD') : '__empty__';

  return (
    <div ref={wrapperRef} style={{ display: 'contents' }}>
      <button
        ref={sentinelRef}
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        style={{ position: 'fixed', top: 0, left: 0, width: 0, height: 0, opacity: 0, border: 0, padding: 0, pointerEvents: 'none' }}
      />
      <DatePicker
        {...rest}
        id={id}
        key={dateKey}
        date={dateMoment}
        onDateChange={(momentDate) => {
          onValueChange(momentDate ? momentDate.format(parseFormat) : '');
        }}
      />
    </div>
  );
};

ManagedDatePicker.propTypes = {
  value: PropTypes.string,
  onValueChange: PropTypes.func.isRequired,
  parseFormat: PropTypes.string,
  id: PropTypes.string.isRequired
};

export default ManagedDatePicker;
